import { useAuth } from "@workos-inc/authkit-react";
import {
	BarChart3,
	Briefcase,
	CalendarDays,
	CircleHelp,
	Clock,
	FileText,
	LogOut,
	Megaphone,
	PanelRightClose,
	PanelRightOpen,
	SquareUser,
	User,
} from "lucide-react";
import type React from "react";
import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSettingsCache } from "@/shared/cache/slices/settings";

import { BrandLogo } from "@/shared/components/BrandLogo";
import {
	ProfileMenu,
	type ProfileMenuItem,
} from "@/shared/components/ProfileMenu/ProfileMenu";
import { Button } from "@/shared/components/ui/button";
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupContent,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	useSidebar,
} from "@/shared/components/ui/sidebar";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { WorkspaceSwitcher } from "@/shared/components/WorkspaceSwitcher/WorkspaceSwitcher";
import { isHourglassInternal } from "@/shared/config/hourglassPermissions";
import { useUpdateNotification } from "@/shared/hooks/useUpdateNotification";

export type AdminView =
	| "dashboard"
	| "billing"
	| "entries"
	| "calendar"
	| "support"
	| "faq"
	| "updates";

export interface AdminSidebarProps {
	permissions: {
		overview: boolean;
		billing: boolean;
		entries: boolean;
		calendar: boolean;
		support: boolean;
	};
}

const navItems: Array<{
	view: AdminView;
	label: string;
	icon: React.ElementType;
	permissionKey: keyof AdminSidebarProps["permissions"];
}> = [
	{
		view: "dashboard",
		label: "Dashboard",
		icon: BarChart3,
		permissionKey: "overview",
	},
	{
		view: "billing",
		label: "Billing",
		icon: FileText,
		permissionKey: "billing",
	},
	{ view: "entries", label: "Entries", icon: Clock, permissionKey: "entries" },
	{
		view: "calendar",
		label: "Calendar",
		icon: CalendarDays,
		permissionKey: "calendar",
	},
];

const AdminSidebar: React.FC<AdminSidebarProps> = ({ permissions }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { user, signOut } = useAuth();
	const { settings } = useSettingsCache();
	const { hasNewUpdate, dismissUpdate } = useUpdateNotification();
	const { state, toggleSidebar } = useSidebar();
	const collapsed = state === "collapsed";
	const showInternalLink = isHourglassInternal(user?.email);

	const currentView: AdminView = useMemo(() => {
		const segments = location.pathname.split("/").filter(Boolean);
		const segment = segments[1] || "dashboard";
		switch (segment) {
			case "billing":
				return "billing";
			case "entries":
				return "entries";
			case "calendar":
				return "calendar";
			case "support":
				return "support";
			case "faq":
				return "faq";
			case "updates":
				return "updates";
			default:
				return "dashboard";
		}
	}, [location.pathname]);

	const userInitials = useMemo(() => {
		if (user?.firstName && user?.lastName) {
			return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
		}
		if (user?.email) {
			return user.email[0].toUpperCase();
		}
		return "?";
	}, [user]);

	const userDisplayName = useMemo(() => {
		if (user?.firstName && user?.lastName) {
			return `${user.firstName} ${user.lastName}`;
		}
		return user?.email ?? "";
	}, [user]);

	const profileMenuItems: ProfileMenuItem[] = [
		...(permissions.support
			? [
					{
						label: "Support",
						icon: User,
						isActive: currentView === "support",
						onClick: () => navigate("/admin/support"),
					},
				]
			: []),
		{
			label: "FAQ",
			icon: CircleHelp,
			isActive: currentView === "faq",
			onClick: () => navigate("/admin/faq"),
			separator: true,
		} as ProfileMenuItem,
		{
			label: "Updates",
			icon: Megaphone,
			isActive: currentView === "updates",
			onClick: () => {
				dismissUpdate();
				navigate("/admin/updates");
			},
			badge: hasNewUpdate,
		},
		{
			label: "Sign out",
			icon: LogOut,
			onClick: () => signOut({ returnTo: window.location.origin }),
			separator: true,
		},
	];

	return (
		<Sidebar
			collapsible="icon"
			className="bg-sidebar border-r border-sidebar-border [&_[data-sidebar=sidebar]]:bg-sidebar"
		>
			<SidebarHeader className="px-4 pt-7 pb-2 text-sidebar-foreground group-data-[collapsible=icon]:px-0">
				<div className="flex h-7 items-center justify-between gap-2 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
					<BrandLogo
						className="h-7 w-auto group-data-[collapsible=icon]:hidden"
						aria-label="Hourglass"
					/>
					<Button
						variant="ghost"
						size="icon"
						onClick={toggleSidebar}
						aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
						className="mb-1 size-9 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground [&_svg]:size-5"
					>
						{collapsed ? <PanelRightClose /> : <PanelRightOpen />}
					</Button>
				</div>
				{collapsed && (
					<>
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									variant="ghost"
									size="icon"
									onClick={() => navigate("/entries")}
									aria-label="Switch to Timekeeper view"
									className="mx-auto mt-3 hidden size-9 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground group-data-[collapsible=icon]:flex [&_svg]:size-5"
								>
									<Briefcase />
								</Button>
							</TooltipTrigger>
							<TooltipContent side="right">Switch to Timekeeper</TooltipContent>
						</Tooltip>
						{showInternalLink && (
							<Tooltip>
								<TooltipTrigger asChild>
									<Button
										variant="ghost"
										size="icon"
										onClick={() => navigate("/internal")}
										aria-label="Switch to Internal view"
										className="mx-auto mt-2 hidden size-9 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground group-data-[collapsible=icon]:flex [&_svg]:size-5"
									>
										<SquareUser />
									</Button>
								</TooltipTrigger>
								<TooltipContent side="right">Switch to Internal</TooltipContent>
							</Tooltip>
						)}
					</>
				)}
				<WorkspaceSwitcher current="admin" />
			</SidebarHeader>

			<SidebarContent>
				<SidebarGroup>
					<SidebarGroupContent>
						<SidebarMenu>
							{navItems.map((item) => {
								if (!permissions[item.permissionKey]) return null;
								return (
									<SidebarMenuItem key={item.view}>
										<SidebarMenuButton
											isActive={currentView === item.view}
											tooltip={item.label}
											onClick={() => navigate(`/admin/${item.view}`)}
											className="h-10 px-3 gap-3 data-[active=true]:font-normal [&>svg]:size-5"
										>
											<item.icon />
											<span>{item.label}</span>
										</SidebarMenuButton>
									</SidebarMenuItem>
								);
							})}
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>
			</SidebarContent>

			<SidebarFooter>
				<ProfileMenu
					userDisplayName={userDisplayName}
					userInitials={userInitials}
					userEmail={user?.firstName ? user.email : undefined}
					subtitle={settings?.prof_type || undefined}
					menuItems={profileMenuItems}
				/>
			</SidebarFooter>
		</Sidebar>
	);
};

export default AdminSidebar;
